import { useState, FormEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const honeypot = form.querySelector<HTMLInputElement>('[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== "") {
      setStatus("success");
      return;
    }
    setStatus("submitting");
    const formData = new FormData(form);
    formData.delete("phone_alt");

    fetch("https://readdy.ai/api/form/d8uiul5u37m8lq7g35u0", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
    })
      .then(() => {
        setStatus("success");
        setEmail("");
      })
      .catch(() => setStatus("error"));
  };

  const column1 = categories.slice(0, 5);
  const column2 = categories.slice(5, 10);
  const column3 = categories.slice(10, 14);

  return (
    <footer className="bg-accent-50/40 border-t border-background-200/70">
      <div className="container-custom py-14 md:py-18">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          <div className="lg:w-[40%]">
            <h3 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
              {t("common:newsletterTitle", "Get The / Best Links")}
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed mb-8 max-w-sm">
              {t("common:newsletterDesc", "Subscribe to receive weekly spreadsheet updates, new product finds, and exclusive buying tips.")}
            </p>
            <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="honeypot-field"
              />
              <div>
                <label htmlFor="footer-email" className="block text-xs font-medium text-foreground-500 mb-1.5">
                  {t("common:emailLabel", "Email")}
                </label>
                <input
                  id="footer-email"
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("common:emailPlaceholder", "Your email address")}
                  className="w-full bg-transparent border-0 border-b border-foreground-300 pb-2.5 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                />
              </div>
              <button
                type="submit"
                disabled={status === "submitting"}
                className="px-6 py-2.5 rounded-full border border-foreground-300 text-sm font-medium text-foreground-700 hover:border-primary-500 hover:text-primary-600 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                {status === "submitting" ? "Sending..." : status === "success" ? "Subscribed!" : t("common:subscribe", "Subscribe")}
              </button>
              {status === "error" && (
                <p className="text-xs text-red-500 mt-1">Something went wrong. Please try again.</p>
              )}
            </form>
          </div>

          <div className="lg:w-[60%] grid grid-cols-2 sm:grid-cols-3 gap-8">
            <div>
              <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-4">{t("common:categories", "Categories")}</h4>
              <ul className="space-y-2.5">
                {column1.map((cat) => (
                  <li key={cat.slug}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-4">&nbsp;</h4>
              <ul className="space-y-2.5">
                {column2.map((cat) => (
                  <li key={cat.slug}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-4">{t("common:siteLinks", "Site Links")}</h4>
              <ul className="space-y-2.5">
                <li>
                  <Link to="/" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap">
                    {t("common:backToHome", "Back to Homepage")}
                  </Link>
                </li>
                <li>
                  <Link to="/allchinabuy-spreadsheet-guide" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap">
                    Buying Guide
                  </Link>
                </li>
                {column3.map((cat) => (
                  <li key={cat.slug}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-background-200/70 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-foreground-500">{t("common:copyright", "© 2026 AllChinaBuy Spreadsheet. All rights reserved.")}</p>
          <div className="flex items-center gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-xs text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              kaspreadsheet.com
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}